import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { buildWhatsAppLink, getContact } from "@/lib/site";
import { useContent } from "@/lib/content";

const COURSE_OPTIONS = ["ACCA", "CMA USA", "CA", "CS", "CFA", "Not sure yet"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConsultationDialog({ open, onOpenChange, source = "consultation-modal" }) {
  const { content } = useContent();
  const c = getContact(content);
  const [form, setForm] = useState({ name: "", phone: "", course: "ACCA", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source });
      toast.success("Counsellor will reach out — opening WhatsApp now.");
      const text = `Hi Educators Institute! I'm ${form.name}. I'd like a free consultation for ${form.course}. Phone: ${form.phone}.`;
      window.open(buildWhatsAppLink(text, c.whatsappRaw), "_blank", "noopener,noreferrer");
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error("Could not save your details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px] rounded-3xl p-0 overflow-hidden border-0 shadow-2xl"
        data-testid="consultation-dialog"
      >
        <div className="bg-[#002570] text-white p-6 md:p-8">
          <DialogHeader>
            <DialogTitle className="font-rubik text-2xl md:text-3xl font-bold leading-tight text-balance">
              Book Your <span className="text-[#73c3ff]">Free Career Consultation</span>
            </DialogTitle>
            <DialogDescription className="font-inter text-sm text-white/80 mt-2">
              A 15-minute call with a real counsellor. No sales pressure.
            </DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={submit} className="p-6 md:p-8 space-y-4 bg-white" data-testid="consultation-dialog-form">
          <div>
            <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Full Name *</label>
            <input
              data-testid="consult-name-input"
              required
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Your name"
              className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
            />
          </div>
          <div>
            <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Phone *</label>
            <input
              data-testid="consult-phone-input"
              required
              type="tel"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="+91 90000 00000"
              className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
            />
          </div>
          <div>
            <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Course of Interest</label>
            <select
              data-testid="consult-course-select"
              value={form.course}
              onChange={(e) => update("course", e.target.value)}
              className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
            >
              {COURSE_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button
            type="submit"
            data-testid="consult-submit-btn"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] disabled:opacity-70 rounded-full px-7 py-3.5 font-medium transition-all"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Booking…</> : <>Book Now & Open WhatsApp <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function useExitIntent(onTrigger, enabled = true) {
  const fired = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    const handler = (e) => {
      if (fired.current) return;
      if (e.clientY <= 0) {
        fired.current = true;
        onTrigger();
      }
    };
    document.addEventListener("mouseout", handler);
    return () => document.removeEventListener("mouseout", handler);
  }, [enabled, onTrigger]);
}
